<template>
    <div class="kgt" :class="{ 'kgt--flat': flat }">
        <div class="kgt-scroll">
            <div v-if="!hideHeader" class="kgt-head" :style="rowStyle">
                <div v-for="h in headers" :key="h.value" class="kgt-th"
                    :class="[alignClass(h), { 'kgt-th--sortable': isSortable(h) }]"
                    @click="isSortable(h) && toggleSort(h.value)">
                    <slot :name="`header.${h.value}`" :header="h">{{ h.text }}</slot>
                    <v-icon v-if="isSortable(h) && innerSortBy === h.value" size="13" class="kgt-sort-ic">
                        {{ innerSortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                    </v-icon>
                </div>
            </div>

            <template v-if="loading">
                <div v-for="n in skeletonCount" :key="'sk' + n" class="kgt-row kgt-row--sk" :style="rowStyle">
                    <div v-for="h in headers" :key="h.value" class="kgt-cell">
                        <span class="kgt-bone" />
                    </div>
                </div>
            </template>
            <div v-else-if="sortedItems.length === 0" class="kgt-empty">{{ noDataText }}</div>
            <template v-else>
                <div v-for="(item, i) in pagedItems" :key="itemKey ? item[itemKey] : i" class="kgt-row"
                    :style="rowStyle">
                    <div v-for="h in headers" :key="h.value" class="kgt-cell" :class="cellClass(h)">
                        <slot :name="`item.${h.value}`" :item="item">{{ item[h.value] }}</slot>
                    </div>
                </div>
            </template>
        </div>

        <div v-if="$slots.footer || $scopedSlots.footer" class="kgt-footer">
            <slot name="footer" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'KimsaGridTable',
    props: {
        headers: { type: Array, default: () => [] },
        items: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        noDataText: { type: String, default: '' },
        itemKey: { type: String, default: '' },
        skeletonCount: { type: Number, default: 8 },
        sortBy: { type: String, default: '' },
        sortDesc: { type: Boolean, default: false },
        disableSort: { type: Boolean, default: false },
        hideHeader: { type: Boolean, default: false },
        flat: { type: Boolean, default: false },
        perPage: { type: Number, default: 0 },
        page: { type: Number, default: 1 },
    },
    data() {
        return {
            innerSortBy: this.sortBy,
            innerSortDesc: this.sortDesc,
        }
    },
    computed: {
        rowStyle(vm = this) {
            return {
                gridTemplateColumns: vm.headers.map(h => h.width || 'minmax(0, 1fr)').join(' '),
                minWidth: vm.minWidth + 'px',
            }
        },
        minWidth(vm = this) {
            return vm.headers.reduce((sum, h) => {
                const match = String(h.width || '').match(/(\d+)px/)
                return sum + (match ? Number(match[1]) : 120)
            }, 0)
        },
        sortedItems(vm = this) {
            if (!vm.innerSortBy) return vm.items
            const key = vm.innerSortBy
            const dir = vm.innerSortDesc ? -1 : 1
            return [...vm.items].sort((a, b) => {
                let av = a[key], bv = b[key]
                if (av == null) av = ''
                if (bv == null) bv = ''
                if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
                return String(av).localeCompare(String(bv)) * dir
            })
        },
        pagedItems(vm = this) {
            if (!vm.perPage) return vm.sortedItems
            const start = (vm.page - 1) * vm.perPage
            return vm.sortedItems.slice(start, start + vm.perPage)
        },
    },
    methods: {
        alignClass(h) {
            if (h.align === 'center') return 'kgt--c'
            if (h.align === 'end' || h.align === 'right') return 'kgt--e'
            return ''
        },
        cellClass(h) {
            return [this.alignClass(h), { 'kgt-sticky': h.stickyEnd }]
        },
        isSortable(h) {
            return !this.disableSort && h.sortable !== false && !!h.value
        },
        toggleSort(value) {
            if (this.innerSortBy === value) this.innerSortDesc = !this.innerSortDesc
            else {
                this.innerSortBy = value
                this.innerSortDesc = false
            }
            this.$emit('update:sort', { sortBy: this.innerSortBy, sortDesc: this.innerSortDesc })
        },
    },
    watch: {
        sortBy(v) { this.innerSortBy = v },
        sortDesc(v) { this.innerSortDesc = v },
    },
}
</script>

<style scoped>
.kgt {
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    background: var(--v-surface-base);
}

.kgt--flat {
    border: none;
    border-radius: 0;
    background: transparent;
}

.kgt-scroll {
    overflow-x: auto;
}

.kgt-scroll::-webkit-scrollbar {
    height: 5px;
}

.kgt-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.kgt-scroll::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 3px;
}

.kgt-head {
    display: grid;
    align-items: center;
    background: var(--v-surface2-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.kgt-th {
    padding: 12px 14px;
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .04em;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
}

.kgt-th--sortable {
    cursor: pointer;
    user-select: none;
}

.kgt-th--sortable:hover {
    color: var(--v-txtSecondary-base);
}

.kgt-sort-ic {
    color: var(--v-secondary-base) !important;
}

.kgt-row {
    display: grid;
    align-items: center;
    min-height: 56px;
    border-bottom: 1px solid var(--v-outline-base);
    transition: background .12s ease;
}

.kgt-row:last-child {
    border-bottom: none;
}

.kgt-row:not(.kgt-row--sk):hover {
    background: var(--v-surface2-base);
}

.kgt-cell {
    padding: 10px 14px;
    font-size: var(--fs-body);
    color: var(--v-txtPrimary-base);
    min-width: 0;
}

@media (min-width: 961px) {
    .kgt-sticky {
        position: sticky;
        right: 0;
        z-index: 1;
    }
}

.kgt--c {
    justify-content: center;
    text-align: center;
}

.kgt--e {
    justify-content: flex-end;
    text-align: right;
}

.kgt-bone {
    display: block;
    height: 14px;
    width: 70%;
    border-radius: 4px;
    background: var(--v-surface2-base);
    animation: kgt-pulse 1.3s ease-in-out infinite;
}

@keyframes kgt-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .5; }
}

.kgt-empty {
    padding: 28px 16px;
    text-align: center;
    font-size: var(--fs-body);
    color: var(--v-txtMuted-base);
}

.kgt-footer {
    border-top: 1px solid var(--v-outline-base);
}
</style>
